import {
  ChangeEvent,
  FunctionComponent,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { ImageUploadType, UploadType, useUpload } from '../../hooks';
import { ImageFile } from '../../models';
import { getCssVar } from '../../utils';
import { Avatar } from '../Avatar';
import { IconLabel } from '../IconLabel';
import { ImageCropModal } from '../ImageCropModal';

import classNames from 'classnames';
import classes from './AvatarUpload.module.scss';

interface AvatarUploadProps {
  avatar: ImageFile | null;
  setAvatar: (image: ImageFile | null) => void;
  avatarSize: number;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  title: string;
  className?: string;
  wrapperClassName?: string;
}

export const AvatarUpload: FunctionComponent<AvatarUploadProps> = memo(
  ({ avatar, setAvatar, avatarSize, loading, setLoading, title, className, wrapperClassName }) => {
    const { t } = useTranslation();

    const { uploadHandler, onUploadCompleted } = useUpload();

    const [imagePreview, setImagePreview] = useState<string | undefined>(avatar?.url);

    const [fileToCrop, setFileToCrop] = useState<File | null>(null);

    const inputFile = useRef<HTMLInputElement | null>(null);

    const renderOverlay = useMemo(() => avatarSize > 140, [avatarSize]);

    const avatarContent = useMemo(() => {
      if (loading) {
        return <Skeleton circle height={'100%'} />;
      }

      const avatarUrl = imagePreview ?? avatar?.url;

      return (
        <>
          <Avatar
            url={avatarUrl}
            size={avatarSize}
            {...(!avatarUrl && { className: classes['avatar-upload__placeholder'] })}
          />

          {renderOverlay && (
            <div className={classes['avatar-upload__overlay']}>
              <IconLabel
                iconId={'photo-upload'}
                className={classes['avatar-upload__overlay-icon']}
                iconSize={20}
                color={getCssVar('--profile-upload-icon-color')}
                singleColor
              />
            </div>
          )}
        </>
      );
    }, [avatar?.url, avatarSize, imagePreview, loading, renderOverlay]);

    const deleteImageHandler = useCallback(() => {
      setImagePreview('');
      setAvatar(null);
    }, [setAvatar]);

    const addOrDelete = useMemo(() => {
      const hasAvatar = Boolean(avatar);

      return (
        <button
          disabled={loading}
          className={classes['avatar-upload__add-or-delete']}
          onClick={hasAvatar ? deleteImageHandler : () => inputFile.current?.click()}
        >
          {t(`avatarUpload.${hasAvatar ? 'delete' : 'add'}-picture`)}
        </button>
      );
    }, [avatar, deleteImageHandler, loading, t]);

    const upload = useCallback(
      async (file: File) => {
        try {
          const { id, assemblyId, isFileTooLarge } = await uploadHandler({
            file,
            type: UploadType.IMAGE,
            imageUploadType: ImageUploadType.PROFILE,
          });

          if (!id) {
            toast.error(t(`fileUpload.${isFileTooLarge ? 'file-too-large-error' : 'error'}`));

            setLoading(false);

            return;
          }

          onUploadCompleted({
            assemblyId,
            callback: () => {
              const url = URL.createObjectURL(file);
              setImagePreview(url);
              setAvatar({ id, url } as ImageFile);
              setLoading(false);
            },
          });
        } catch (e) {}
      },
      [onUploadCompleted, setAvatar, setLoading, t, uploadHandler]
    );

    const onCrop = useCallback(
      (file: File) => {
        setFileToCrop(null);
        setLoading(true);
        upload(file);
      },
      [setLoading, upload]
    );

    const onCropClose = useCallback(() => {
      setFileToCrop(null);
    }, []);

    const onFileChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
      const file = target.files?.[0];

      if (file) {
        setFileToCrop(file);
      }
    };

    return (
      <div className={classNames(classes['avatar-upload'], className)}>
        <label
          className={classNames(classes['avatar-upload__wrapper'], wrapperClassName)}
          style={{ width: `${avatarSize}px`, height: `${avatarSize}px` }}
          htmlFor={'avatar'}
        >
          {avatarContent}
        </label>

        <input
          ref={inputFile}
          id={'avatar'}
          type={'file'}
          accept={'image/*'}
          className={classes['avatar-upload__file']}
          onChange={onFileChange}
          onClick={({ target }) => ((target as HTMLInputElement).value = '')}
          disabled={loading}
        />

        {addOrDelete}

        {fileToCrop && (
          <ImageCropModal
            file={fileToCrop}
            title={title}
            onCrop={onCrop}
            onClose={onCropClose}
            circularCrop
          />
        )}
      </div>
    );
  }
);
